import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider, Snackbar, Alert, Button } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';

// eslint-disable-next-line import/no-unresolved
import SidebarProvider from './contexts/SidebarContext';
import { ChannelsProvider } from './contexts/ChannelsContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth, useOrgLinkBranding } from './contexts/AuthContext';
import { useAppStore } from './hooks/useAppStore';
import { useReleaseNotesStore } from './hooks/useReleaseNotesStore';
import { DragProvider } from './contexts/DragContext';
import NotifierProvider from './contexts/NotifierContext';

import { redirectToLogin } from './utils/backendUrl';
import { applyNeutralHeadBranding } from './utils/neutralBranding';
import { authService } from './services/authService';
import ReconnectionPopup from './components/ui/ReconnectionPopup';
import PendingApprovalPage from './components/PendingApprovalPage';
import LinkExpiredPage from './components/LinkExpiredPage';
import TabInactivePage from './components/TabInactivePage';
import { networkEventManager } from './services/networkEventManager';
import { tabCoordinationService } from './services/tabCoordinationService';
import Layout from './components/Layout';
import { muiTheme } from './theme/muiTheme';

import './App.css';
import { useVersionCheck } from './hooks/useVersionCheck';

// Extracted Shell component outside of App to properly use hooks
const Shell: React.FC = observer(() => {
  const { user, loading, loggingOut, pendingApproval } = useAuth();
  const appStore = useAppStore();
  const navigate = useNavigate();
  const hasScheduledRedirectRef = useRef(false);

  const [showReconnectionPopup, setShowReconnectionPopup] = useState(false);
  const { updateAvailable, dismiss } = useVersionCheck();
  const { versionNotifications, clearVersionNotifications } = useReleaseNotesStore();

  // Sync the authenticated user's email to AppStore so stores can use it
  useEffect(() => {
    appStore.setUserEmail(user?.email ?? null);
  }, [user?.email, appStore]);

  // Organisation links can white-label the browser tab title (REV-2370).
  const { pageTitle, whitelabel } = useOrgLinkBranding();
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  useEffect(() => (whitelabel ? applyNeutralHeadBranding() : undefined), [whitelabel]);

  useEffect(() => {
    // Don't redirect to login if user is pending approval (they're authenticated but not authorized),
    // if the preauth bound-chat link has expired (they should see LinkExpiredPage, not the login screen),
    // or if this tab has been deactivated by another active tab (they should see TabInactivePage)
    if (!loading && !loggingOut && !user && !pendingApproval && !appStore.isPreauthExpired && !appStore.isTabInactive && !hasScheduledRedirectRef.current) {
      hasScheduledRedirectRef.current = true;
      redirectToLogin();
    }
  }, [user, loading, loggingOut, pendingApproval, appStore.isPreauthExpired, appStore.isTabInactive, appStore]);

  // Ensure only one tab stays active: the newest tab/refresh claims active status and
  // every other open tab deactivates itself (closes its websocket, shows TabInactivePage)
  useEffect(() => {
    const handleTabDeactivated = () => {
      import('./services/websocketService').then(({ websocketService }) => {
        websocketService.disconnect();
      });
      networkEventManager.emitTabDeactivated();
    };

    tabCoordinationService.init(handleTabDeactivated);

    return () => {
      tabCoordinationService.destroy();
    };
  }, []);

  // Listen for network events
  useEffect(() => {
    const handleNetworkOffline = () => {
      setShowReconnectionPopup(true);
    };
    
    const handlePopupClose = () => {
      setShowReconnectionPopup(false);
      import('./services/reconnectionTimerManager').then(({ reconnectionTimerManager }) => {
        reconnectionTimerManager.reset();
      });
    };

    networkEventManager.on('network:offline', handleNetworkOffline);
    networkEventManager.on('auth:failure', handlePopupClose);
    networkEventManager.on('connection:recovered', handlePopupClose);

    return () => {
      networkEventManager.off('network:offline', handleNetworkOffline);
      networkEventManager.off('auth:failure', handlePopupClose);
      networkEventManager.off('connection:recovered', handlePopupClose);
    };
  }, []);

  // Phase 3: Initialize user-level WebSocket connection
  useEffect(() => {
    if (!user || loading || loggingOut || appStore.isTabInactive) {
      return;
    }

    const initializeWebSocket = async () => {
      try {
        // Check if user has valid session
        const hasSession = await authService.checkSession();

        if (!hasSession) {
          console.log('[App] No valid session, will redirect to login');
          return;
        }

        // Import websocketService dynamically
        const { websocketService } = await import('./services/websocketService');

        // Connect user WebSocket (single connection for all chats)
        const connected = await websocketService.connectUser();

        if (connected) {
          console.log('[App] User WebSocket connected, token refresh started');
        } else {
          console.warn('[App] Failed to connect user WebSocket');
        }
      } catch (error) {
        console.error('[App] WebSocket initialization error:', error);
      }
    };

    initializeWebSocket();

    // Cleanup on unmount or logout
    return () => {
      import('./services/websocketService').then(({ websocketService }) => {
        websocketService.disconnect();
      });
    };
  }, [user, loading, loggingOut, appStore.isTabInactive]);

  // Handle reconnection attempts (Phase 3: Updated for user WebSocket)
  const handleReconnection = React.useCallback(async () => {
    // Import websocketService dynamically to avoid circular dependencies
    const { websocketService } = await import('./services/websocketService');

    try {
      // Check backend connectivity first
      const pingResult = await authService.ping();

      if (!pingResult.success) {
        if (pingResult.status === 401 || pingResult.status === 403) {
          // Session expired - emit auth failure event to close popup and redirect
          networkEventManager.emitAuthFailure('Session expired during reconnection');
          return false;
        }
        return false;
      }

      // Reconnect user WebSocket
      const connected = await websocketService.connectUser();

      if (connected) {
        console.log('[App] Reconnection successful');
        // Emit connection recovered event to close popup
        networkEventManager.emitConnectionRecovered();
        return true;
      }

      console.warn('[App] Reconnection failed');
      return false;
    } catch (error) {
      console.error('[App] Reconnection error:', error);
      return false;
    }
  }, []);

  if (appStore.isTabInactive) return <TabInactivePage />;
  if (loading || loggingOut) return <div className="p-6" />;
  if (pendingApproval) return <PendingApprovalPage />;
  if (appStore.isPreauthExpired) return <LinkExpiredPage />;
  if (!user) return <div className="p-6" />;

  return (
    <NotifierProvider>
      <ChannelsProvider>
        <SidebarProvider>
          <DragProvider>
            <div className="h-full overflow-hidden">
              <Layout />

              <ReconnectionPopup
                isVisible={showReconnectionPopup}
                onRetry={handleReconnection}
                onClose={() => setShowReconnectionPopup(false)}
              />

              <Snackbar
                open={updateAvailable}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                onClose={(_, reason) => {
                  if (reason === 'clickaway') return;
                  dismiss();
                }}
              >
                <Alert
                  severity="info"
                  variant="filled"
                  onClose={dismiss}
                  action={
                    <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                      Refresh
                    </Button>
                  }
                >
                  A new version is available.
                </Alert>
              </Snackbar>

              <Snackbar
                open={!!versionNotifications}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                autoHideDuration={10000}
                onClose={(_, reason) => {
                  if (reason === 'clickaway') return;
                  clearVersionNotifications();
                }}
              >
                <Alert
                  severity="info"
                  variant="filled"
                  onClose={clearVersionNotifications}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      sx={{ 
                        textDecoration: "underline !important", 
                        padding: "0px", 
                        fontSize: "12px", 
                        verticalAlign: "center", 
                        paddingTop: "2px", 
                        fontWeight: "bold !important" 
                      }}
                      onClick={() => {
                        clearVersionNotifications();
                        navigate('/release-notes');
                      }}
                    >
                      See what's new!
                    </Button>
                  }
                >
                  A new app release has been published since your last login.
                </Alert>
              </Snackbar>
            </div>
          </DragProvider>
        </SidebarProvider>
      </ChannelsProvider>
    </NotifierProvider>
  );
});

// Converted App to functional component
const App: React.FC = () => {
  return (
    <ThemeProvider theme={muiTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default App;